import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  SsoDiscoverRequestDto,
  SsoDiscoverResponseDto
} from './vive-api-models';
import viveApiConfig from './vive-api.config';

class SsoService extends ViveApiBaseService {
  // Asked before anyone signs in, so it sends no token. The answer says only
  // whether the email's domain has active SSO, never whether a user exists.
  public async discover(params: {
    options?: ViveApiRequestOptions;
    data: SsoDiscoverRequestDto;
  }): Promise<SsoDiscoverResponseDto> {
    const { options, data } = params;

    return await this.sendPost<SsoDiscoverResponseDto>({
      apiEndpoint: 'auth/sso/discover',
      options,
      authRequired: false,
      data
    });
  }

  // Builds the URL only: navigate the window to it (window.location.assign).
  // A fetch cannot sign anyone in here, because the route sets the binding
  // cookie and redirects to the identity provider, which a fetch cannot follow.
  // `returnTo` must be on an origin the API allows, or the API answers 400.
  public startUrl(params: {
    connectionId: number;
    returnTo: string;
    loginHint?: string;
  }): string {
    const { connectionId, returnTo, loginHint } = params;
    const url = new URL(
      `${viveApiConfig.getViveApiBaseUrl()}/api/v3/auth/sso/${encodeURIComponent(String(connectionId))}/start`
    );
    url.searchParams.set('returnTo', returnTo);
    if (loginHint !== undefined) {
      url.searchParams.set('login_hint', loginHint);
    }

    return url.href;
  }

  // The SAML SP metadata XML an IdP admin imports, as a file. Served for every
  // connection status, so it can be downloaded while the connection is draft.
  // It sends no token: the route is public, because the IdP reads it too.
  public async getSamlMetadata(params: {
    options?: ViveApiRequestOptions;
    connectionId: number;
  }): Promise<Blob> {
    const { options, connectionId } = params;

    return await this.sendGet<Blob>({
      apiEndpoint: 'auth/sso/saml/:connectionId/metadata',
      pathParams: { connectionId },
      options: { ...options, responseType: 'blob' },
      authRequired: false
    });
  }
}

export const ssoService = new SsoService();
